import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, FormControl, FormControlLabel, FormLabel, MenuItem, Paper, Radio, RadioGroup,
  Stack, TextField, Typography,
} from '@mui/material';
import { CheckCircle as DoneIcon } from '@mui/icons-material';
import { calendarService } from '../../services/api';
import { visitorId } from '../../utils/trackPageView';

// Sondage « calendrier papier » : page publique atteinte par un lien (message a tous). Aucune reponse par message :
// tout se range dans l'administration (page Calendrier papier). Une reponse par visiteur, modifiable en revenant ici.
// Les tranches de prix doivent rester alignees sur backend/src/utils/calendarPrices.js.
const PRICES = {
  cameroun: [
    { key: 'cm_1', fr: 'Moins de 3 000 FCFA', en: 'Less than 3,000 FCFA' },
    { key: 'cm_2', fr: '3 000 à 5 000 FCFA', en: '3,000 to 5,000 FCFA' },
    { key: 'cm_3', fr: '5 000 à 8 000 FCFA', en: '5,000 to 8,000 FCFA' },
    { key: 'cm_4', fr: '8 000 à 12 000 FCFA', en: '8,000 to 12,000 FCFA' },
    { key: 'cm_5', fr: 'Plus de 12 000 FCFA', en: 'More than 12,000 FCFA' },
  ],
  etranger: [
    { key: 'ex_1', fr: 'Moins de 8 €', en: 'Less than €8' },
    { key: 'ex_2', fr: '8 à 12 €', en: '€8 to €12' },
    { key: 'ex_3', fr: '12 à 18 €', en: '€12 to €18' },
    { key: 'ex_4', fr: '18 à 25 €', en: '€18 to €25' },
    { key: 'ex_5', fr: 'Plus de 25 €', en: 'More than €25' },
  ],
};

const T = {
  fr: {
    title: 'Un calendrier Éphéméride en papier ?',
    intro: "Chaque matin, vous recevez un proverbe duala. Nous pensons en faire un vrai calendrier à poser sur un bureau : une page par jour à détacher, avec le proverbe en duala, sa traduction et son sens, et un socle en carton. Avant de lancer l'impression, nous voulons savoir s'il vous intéresserait.",
    support: "Le site est gratuit et le restera. Ce calendrier servirait aussi à financer son hébergement.",
    maquette: "Maquette indicative : le rendu final peut différer.",
    q1: 'Seriez-vous intéressé(e) ?',
    yes: 'Oui, certainement', maybe: 'Peut-être, selon le prix', no: 'Non, merci',
    q2: 'Où le recevriez-vous ?',
    cm: 'Au Cameroun', ex: 'En dehors du Cameroun',
    q3: 'Quel prix vous semblerait juste ?',
    q3hint: "Il n'y a pas de bonne réponse : aucun prix n'est encore fixé, votre avis nous aide à le choisir.",
    q4: "Combien d'exemplaires ?",
    contactTitle: 'Être prévenu(e) du lancement (facultatif)',
    contactText: "Laissez un numéro WhatsApp ou un email si vous voulez être prévenu(e) quand les précommandes ouvriront. Ces informations ne servent qu'à cela et ne sont jamais partagées.",
    name: 'Votre nom', contact: 'Numéro WhatsApp ou email', city: 'Ville',
    send: 'Envoyer ma réponse', sending: 'Envoi...',
    thanks: 'Merci pour votre réponse !', thanksText: "Elle nous aide à décider. Vous pouvez la modifier à tout moment en revenant sur cette page.",
    thanksUpdated: 'Votre réponse a été mise à jour. Merci !',
    again: 'Modifier ma réponse', required: 'Merci de répondre aux questions marquées.',
    error: "Une erreur s'est produite, réessayez dans un instant.",
  },
  en: {
    title: 'A paper Éphéméride calendar?',
    intro: 'Every morning you receive a Duala proverb. We are thinking of turning it into a real desk calendar: one tear-off page per day, with the Duala proverb, its translation and its meaning, on a cardboard stand. Before printing, we would like to know whether you would be interested.',
    support: 'The website is free and will stay free. This calendar would also help pay for its hosting.',
    maquette: 'Indicative mock-up: the final result may differ.',
    q1: 'Would you be interested?',
    yes: 'Yes, definitely', maybe: 'Maybe, depending on the price', no: 'No, thank you',
    q2: 'Where would you receive it?',
    cm: 'In Cameroon', ex: 'Outside Cameroon',
    q3: 'What price would seem fair to you?',
    q3hint: 'There is no right answer: no price is set yet, your opinion helps us choose it.',
    q4: 'How many copies?',
    contactTitle: 'Be told when it launches (optional)',
    contactText: 'Leave a WhatsApp number or an email if you want to be told when pre-orders open. This information is used only for that and is never shared.',
    name: 'Your name', contact: 'WhatsApp number or email', city: 'City',
    send: 'Send my answer', sending: 'Sending...',
    thanks: 'Thank you for your answer!', thanksText: 'It helps us decide. You can change it at any time by coming back to this page.',
    thanksUpdated: 'Your answer has been updated. Thank you!',
    again: 'Change my answer', required: 'Please answer the marked questions.',
    error: 'Something went wrong, please try again in a moment.',
  },
};

function pickLanguage(param) {
  if (T[param]) return param;
  const nav = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'fr').slice(0, 2).toLowerCase();
  return nav === 'en' ? 'en' : 'fr';
}

function CalendarInterest() {
  const [params] = useSearchParams();
  const lang = pickLanguage(params.get('lang'));
  const t = T[lang];

  const [interest, setInterest] = useState('');
  const [region, setRegion] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [city, setCity] = useState('');
  const [trap, setTrap] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // null | 'created' | 'updated'
  const [error, setError] = useState('');

  // Page sans interet pour les moteurs de recherche
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const wantsPrice = interest === 'oui' || interest === 'peut_etre';
  const complete = interest && region && (!wantsPrice || price);

  const submit = async (e) => {
    e.preventDefault();
    if (!complete) { setError(t.required); return; }
    setSending(true);
    setError('');
    try {
      const res = await calendarService.submit({
        visitor: visitorId() || `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        interest, region, price_key: wantsPrice ? price : null, quantity: wantsPrice ? quantity : 1,
        contact_name: name, contact, city, lang, website: trap,
      });
      setDone(res.data.updated ? 'updated' : 'created');
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || t.error);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', borderRadius: 3 }}>
        <DoneIcon color="success" sx={{ fontSize: 56 }} />
        <Typography variant="h5" component="h1" sx={{ mt: 1, mb: 1, fontWeight: 700 }}>{done === 'updated' ? t.thanksUpdated : t.thanks}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>{t.thanksText}</Typography>
        <Button variant="outlined" onClick={() => setDone(null)}>{t.again}</Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.6rem', sm: '2.1rem' } }}>{t.title}</Typography>

      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box
          component="img"
          src="/calendrier/apercu.jpg"
          alt=""
          sx={{ width: '100%', maxWidth: 340, height: 'auto', borderRadius: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t.maquette}</Typography>
      </Box>

      <Typography sx={{ mb: 1.5 }}>{t.intro}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{t.support}</Typography>

      <Box component="form" onSubmit={submit} noValidate>
        <Stack spacing={3}>
          <FormControl>
            <FormLabel sx={{ fontWeight: 700, color: 'text.primary' }}>{t.q1} *</FormLabel>
            <RadioGroup value={interest} onChange={(e) => setInterest(e.target.value)}>
              <FormControlLabel value="oui" control={<Radio />} label={t.yes} />
              <FormControlLabel value="peut_etre" control={<Radio />} label={t.maybe} />
              <FormControlLabel value="non" control={<Radio />} label={t.no} />
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel sx={{ fontWeight: 700, color: 'text.primary' }}>{t.q2} *</FormLabel>
            <RadioGroup value={region} onChange={(e) => { setRegion(e.target.value); setPrice(''); }}>
              <FormControlLabel value="cameroun" control={<Radio />} label={t.cm} />
              <FormControlLabel value="etranger" control={<Radio />} label={t.ex} />
            </RadioGroup>
          </FormControl>

          {wantsPrice && region && (
            <>
              <FormControl>
                <FormLabel sx={{ fontWeight: 700, color: 'text.primary' }}>{t.q3} *</FormLabel>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>{t.q3hint}</Typography>
                <RadioGroup value={price} onChange={(e) => setPrice(e.target.value)}>
                  {PRICES[region].map((o) => <FormControlLabel key={o.key} value={o.key} control={<Radio />} label={o[lang]} />)}
                </RadioGroup>
              </FormControl>

              <TextField select label={t.q4} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} sx={{ maxWidth: 200 }}>
                {[1, 2, 3, 4, 5, 10, 20].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
            </>
          )}

          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>{t.contactTitle}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t.contactText}</Typography>
            <Stack spacing={1.5}>
              <TextField size="small" label={t.name} value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100, autoComplete: 'name' }} />
              <TextField size="small" label={t.contact} value={contact} onChange={(e) => setContact(e.target.value)} inputProps={{ maxLength: 150 }} />
              <TextField size="small" label={t.city} value={city} onChange={(e) => setCity(e.target.value)} inputProps={{ maxLength: 80 }} />
            </Stack>
          </Box>

          {/* champ piege contre les robots : invisible pour les humains */}
          <Box aria-hidden="true" sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)} />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <Button type="submit" variant="contained" size="large" disabled={sending} startIcon={sending ? <CircularProgress size={18} color="inherit" /> : null}>
            {sending ? t.sending : t.send}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

export default CalendarInterest;
